/**
 * Serialize Counterpunch model objects into the font-editor clipboard envelope.
 */
import { DecomposedAffineTransform, Layer } from '../babelfont-model';
import { COUNTERPUNCH_CLIPBOARD_VENDOR, COUNTERPUNCH_CLIPBOARD_VERSION, FONT_EDITOR_CLIPBOARD_SCHEMA, FONT_EDITOR_CLIPBOARD_SCHEMA_VERSION } from './json';
export { COUNTERPUNCH_CLIPBOARD_VERSION };
const GLYPHS_ATTR_KEY = 'com.schriftgestalt.Glyphs.attr';
export function buildSelectionClipboardDocument(options) {
    const { paths, components, anchors, guides } = options;
    if (paths.length === 0 &&
        components.length === 0 &&
        anchors.length === 0 &&
        guides.length === 0) {
        return null;
    }
    return {
        version: COUNTERPUNCH_CLIPBOARD_VERSION,
        kind: 'selection',
        nodeOrder: 'start-first',
        keepAbsoluteCoords: true,
        glyph: options.glyphName ?? null,
        layerId: options.layerId ?? null,
        paths,
        components,
        anchors,
        guides
    };
}
export function buildGlyphsClipboardDocument(glyphs, masters) {
    if (glyphs.length === 0 || masters.length === 0) {
        return null;
    }
    return {
        version: COUNTERPUNCH_CLIPBOARD_VERSION,
        kind: 'glyphs',
        nodeOrder: 'start-first',
        masters,
        glyphs
    };
}
export function wrapClipboardEnvelope(document, extraItems) {
    return {
        clipboardSchema: FONT_EDITOR_CLIPBOARD_SCHEMA,
        clipboardSchemaVersion: FONT_EDITOR_CLIPBOARD_SCHEMA_VERSION,
        clipboardItems: {
            ...(extraItems || {}),
            [COUNTERPUNCH_CLIPBOARD_VENDOR]: document
        }
    };
}
export function serializeFontMastersForClipboard(font) {
    return (font.masters || []).map((master) => {
        const location = plainLocation(master.location);
        return {
            id: master.id,
            name: masterDisplayName(master),
            ...(location ? { location } : {})
        };
    });
}
export function serializeGlyphForClipboard(glyph) {
    const font = glyph.parent();
    const masterIds = (font?.masters || []).map((master) => master.id);
    const masterIndexById = new Map(masterIds.map((id, index) => [id, index]));
    const baseLayers = [];
    const featureVariationMap = new Map();
    for (const layer of getRawForegroundLayers(glyph)) {
        const serialized = serializeLayerForClipboard(layer, masterIndexById);
        if (!serialized) {
            continue;
        }
        const axisRules = readFeatureVariationAxisRules(layer);
        if (axisRules) {
            const familyId = JSON.stringify(axisRules);
            let family = featureVariationMap.get(familyId);
            if (!family) {
                family = { axisRules, layers: [] };
                featureVariationMap.set(familyId, family);
            }
            family.layers.push(serialized);
            continue;
        }
        baseLayers.push(serialized);
    }
    const featureVariations = [
        ...featureVariationMap.values()
    ];
    return {
        name: glyph.name,
        leftMetricsKey: glyph.leftMetricsKey ?? null,
        rightMetricsKey: glyph.rightMetricsKey ?? null,
        layers: baseLayers,
        ...(featureVariations.length > 0 ? { featureVariations } : {})
    };
}
export function serializeLayerForClipboard(layer, masterIndexById) {
    const master = serializeLayerMaster(layer, masterIndexById);
    if (!master) {
        return null;
    }
    const paths = [];
    const components = [];
    for (const shape of layer.shapes || []) {
        if (shape.isPath?.()) {
            paths.push(serializePathForClipboard(shape.asPath()));
        }
        else if (shape.isComponent?.()) {
            components.push(serializeComponentForClipboard(shape.asComponent()));
        }
    }
    const location = plainLocation(layer.location);
    return {
        layerId: layer.id,
        name: layer.name,
        master,
        ...(location ? { location } : {}),
        width: Number(layer.width),
        leftMetricsKey: layer.leftMetricsKey ?? null,
        rightMetricsKey: layer.rightMetricsKey ?? null,
        paths,
        components,
        anchors: (layer.anchors || [])
            .filter((anchor) => !!anchor.name)
            .map(serializeAnchorForClipboard),
        guides: (layer.guides || []).map((guide) => serializeGuideForClipboard(guide, false))
    };
}
export function serializePathForClipboard(path) {
    const nodes = [];
    for (const node of path.nodes || []) {
        const serialized = serializeNodeForClipboard(node);
        if (serialized) {
            nodes.push(serialized);
        }
    }
    const closed = !!path.closed;
    return {
        closed,
        nodes: ensureStartFirstNodes(nodes, closed),
        ...(path.format_specific !== undefined
            ? { format_specific: path.format_specific }
            : {})
    };
}
/** Rotate Glyphs-ordered closed contours so the start on-curve is index 0. */
function ensureStartFirstNodes(nodes, closed) {
    if (!closed || nodes.length < 2) {
        return nodes;
    }
    if (nodes[0].nodetype !== 'OffCurve') {
        return nodes;
    }
    const last = nodes[nodes.length - 1];
    if (last.nodetype === 'OffCurve') {
        return nodes;
    }
    return [last, ...nodes.slice(0, -1)];
}
export function serializeComponentForClipboard(component) {
    const transform = toAffineTuple(component.transform);
    return {
        reference: component.reference,
        x: transform[4],
        y: transform[5],
        transform,
        alignment: component.automaticAlignment ? 1 : 0,
        ...(component.anchor ? { anchor: component.anchor } : {}),
        ...(component.format_specific !== undefined
            ? { format_specific: component.format_specific }
            : {})
    };
}
export function serializeAnchorForClipboard(anchor) {
    return {
        name: anchor.name || '',
        x: Number(anchor.x),
        y: Number(anchor.y),
        ...(anchor.format_specific !== undefined
            ? { format_specific: anchor.format_specific }
            : {})
    };
}
export function serializeGuideForClipboard(guide, globalGuide) {
    const x = Number(guide.pos?.x ?? guide.x ?? 0);
    const y = Number(guide.pos?.y ?? guide.y ?? 0);
    const angle = Number(guide.pos?.angle ?? guide.angle ?? 0);
    return {
        ...(guide.name ? { name: guide.name } : {}),
        x,
        y,
        angle,
        global: globalGuide,
        ...(guide.format_specific !== undefined
            ? { format_specific: guide.format_specific }
            : {})
    };
}
export function serializeMasterGuideForClipboard(guide) {
    return serializeGuideForClipboard(guide, true);
}
export function stringifyClipboardDocument(document) {
    return JSON.stringify(wrapClipboardEnvelope(document), null, 4);
}
export function summarizeClipboardDocument(document) {
    if (document.kind === 'selection') {
        const counts = [];
        const entries = [
            ['paths', 'path'],
            ['components', 'component'],
            ['anchors', 'anchor'],
            ['guides', 'guide']
        ];
        for (const [key, label] of entries) {
            const value = document[key];
            const count = Array.isArray(value) ? value.length : 0;
            if (count > 0) {
                counts.push(`${count} ${label}${count === 1 ? '' : 's'}`);
            }
        }
        const detail = counts.length > 0 ? counts.join(', ') : 'nothing';
        return `Copied ${detail} from /${document.glyph || '?'}`;
    }
    const count = document.glyphs.length;
    return `Copied ${count} glyph${count === 1 ? '' : 's'}`;
}
function serializeNodeForClipboard(node) {
    const rawType = String(node.nodetype || node.type || '');
    const nodetype = normalizeNodeType(rawType);
    if (!nodetype) {
        return null;
    }
    return {
        x: Number(node.x),
        y: Number(node.y),
        nodetype,
        ...(node.smooth && nodetype !== 'OffCurve' ? { smooth: true } : {})
    };
}
function normalizeNodeType(value) {
    switch (value) {
        case 'Line':
        case 'Curve':
        case 'OffCurve':
        case 'QCurve':
        case 'Move':
            return value;
        case 'line':
            return 'Line';
        case 'curve':
            return 'Curve';
        case 'offcurve':
        case 'o':
            return 'OffCurve';
        case 'qcurve':
            return 'QCurve';
        case 'move':
            return 'Move';
        default:
            return null;
    }
}
function toAffineTuple(transform) {
    if (Array.isArray(transform) && transform.length >= 6) {
        return [
            Number(transform[0]) || 0,
            Number(transform[1]) || 0,
            Number(transform[2]) || 0,
            Number(transform[3]) || 0,
            Number(transform[4]) || 0,
            Number(transform[5]) || 0
        ];
    }
    if (transform && typeof transform === 'object') {
        return DecomposedAffineTransform.toAffine(transform);
    }
    return DecomposedAffineTransform.toAffine(DecomposedAffineTransform.identity());
}
function getRawForegroundLayers(glyph) {
    const layersData = glyph
        .data?.layers;
    if (!Array.isArray(layersData)) {
        return [];
    }
    const layers = [];
    for (let index = 0; index < layersData.length; index++) {
        const layer = new Layer(layersData, index, glyph);
        if (layer.is_background) {
            continue;
        }
        layers.push(layer);
    }
    return layers;
}
function serializeLayerMaster(layer, masterIndexById) {
    const master = layer.master;
    if (!master || typeof master !== 'object' || !('type' in master)) {
        return null;
    }
    if (master.type === 'FreeFloating') {
        return { type: 'FreeFloating' };
    }
    if (master.type !== 'DefaultForMaster' &&
        master.type !== 'AssociatedWithMaster') {
        return null;
    }
    const masterId = master.master;
    if (typeof masterId !== 'string' || !masterId) {
        return null;
    }
    const masterIndex = masterIndexById?.get(masterId);
    if (masterIndex === undefined) {
        return null;
    }
    return { type: master.type, masterIndex };
}
function readFeatureVariationAxisRules(layer) {
    const attributes = layer.format_specific?.[GLYPHS_ATTR_KEY];
    if (!attributes ||
        typeof attributes !== 'object' ||
        Array.isArray(attributes)) {
        return null;
    }
    const axisRules = attributes.axisRules;
    return Array.isArray(axisRules) ? axisRules : null;
}
function plainLocation(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    const location = {};
    for (const [key, raw] of Object.entries(value)) {
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(num)) {
            continue;
        }
        location[key] = num;
    }
    return Object.keys(location).length > 0 ? location : null;
}
function masterDisplayName(master) {
    const name = master.toJSON?.()?.name;
    if (typeof name === 'string' && name) {
        return name;
    }
    if (name && typeof name === 'object' && !Array.isArray(name)) {
        const record = name;
        for (const key of ['dflt', 'en']) {
            if (typeof record[key] === 'string' && record[key]) {
                return record[key];
            }
        }
        for (const value of Object.values(record)) {
            if (typeof value === 'string' && value) {
                return value;
            }
        }
    }
    return master.id;
}
